// Shrinks a photo in the browser before it goes to the server: the identity
// check only needs a readable face / document, not a 12-megapixel original.

export const MAX_PHOTO_EDGE = 1600;
export const PHOTO_MIME = 'image/jpeg';

/** Longest edge ≤ `maxEdge`, preserving the aspect ratio. */
export function fitWithin(width: number, height: number, maxEdge: number = MAX_PHOTO_EDGE): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** "data:image/jpeg;base64,AAAA" → "AAAA". */
export function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-load-failed'));
    };
    img.src = url;
  });
}

/**
 * Re-encodes the picked file as a JPEG no larger than 1600px on its longest
 * edge. Returns the base64 body (no `data:` prefix), its mime type, and a
 * preview URL for the screen.
 */
export async function preparePhoto(file: Blob, quality = 0.85): Promise<{ imageBase64: string; mimeType: typeof PHOTO_MIME; previewUrl: string }> {
  const img = await loadImage(file);
  const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  const previewUrl = canvas.toDataURL(PHOTO_MIME, quality);
  return { imageBase64: stripDataUrl(previewUrl), mimeType: PHOTO_MIME, previewUrl };
}
